import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  LinkOutlined,
  ReloadOutlined,
  RetweetOutlined,
} from '@ant-design/icons';

import {
  AnswerKeyGateTag,
  AnswerKeyRating,
  ConvictionCoverageNote,
  ParticipationNote,
  VerdictBanner,
  readAnswerKeyGate,
} from 'components';

import { fetchScenarioValidity } from '../ScenarioBuilder/actionsApi';
import {
  attachAssessmentToSession,
  downloadAssessmentResults,
  fetchAssessmentResultDetail,
  fetchAssessmentResults,
  recomputeAssessment,
  validateAssessmentScenario,
} from './api';
import {
  EllipsisText,
  formatDateTime,
  formatScore,
  JsonBlock,
  QueueStatusTag,
  ValidationPanel,
} from './shared';
import {
  AssessmentResult,
  AssessmentResultDetail,
  AssessmentResultsResponse,
  AssessmentScenario,
  AssessmentSessionLink,
  AssessmentValidation,
} from './types';

type Props = {
  scenario: AssessmentScenario;
  links: AssessmentSessionLink[];
  onLinksChanged: () => Promise<void> | void;
};

const SessionBlueprint: React.FC<{ link: AssessmentSessionLink }> = ({
  link,
}) => {
  if (!link.blueprint)
    return (
      <Typography.Text type="secondary">
        No recorded blueprint launch
      </Typography.Text>
    );
  return (
    <Space direction="vertical" size={0}>
      <EllipsisText value={link.blueprint.blueprint_name} />
      <Typography.Text type="secondary">
        Version {link.blueprint.version_number} · variant #
        {link.blueprint.variant_id} · {link.blueprint.difficulty}
      </Typography.Text>
    </Space>
  );
};

const PillarDetail: React.FC<{
  detail: AssessmentResultDetail;
  pillar: string;
}> = ({ detail, pillar }) => {
  const value = detail.pillars[pillar] || {};
  if (pillar === '4') {
    return (
      <div className="assessment-detail-stack">
        <Card size="small" title="Components">
          <JsonBlock value={value.components} />
        </Card>
        <Card size="small" title="Transitions">
          <JsonBlock value={value.transitions} />
        </Card>
        <Card size="small" title="Diagnostics">
          <JsonBlock value={value.diagnostics} />
        </Card>
        <Card size="small" title="Evidence coverage">
          <JsonBlock value={value.evidence_coverage} />
        </Card>
      </div>
    );
  }
  return (
    <Table
      size="small"
      className="assessment-table"
      rowKey="period"
      pagination={false}
      dataSource={value.periods || []}
      columns={[
        { title: 'Period', dataIndex: 'period', width: 75 },
        { title: 'Label', dataIndex: 'label', width: 100 },
        { title: 'Regime', dataIndex: 'regime', width: 220 },
        { title: 'Score', dataIndex: 'score', width: 95, render: formatScore },
        {
          title: 'Runtime components',
          dataIndex: 'components',
          render: value => <JsonBlock compact value={value} />,
        },
      ]}
    />
  );
};

const SessionsResults: React.FC<Props> = ({
  scenario,
  links,
  onLinksChanged,
}) => {
  const [attachForm] = Form.useForm();
  const [attaching, setAttaching] = useState(false);
  const [selectedSession, setSelectedSession] = useState<number | undefined>(
    links[0]?.session_id,
  );
  const [results, setResults] = useState<AssessmentResultsResponse | null>(
    null,
  );
  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState('');
  const [detail, setDetail] = useState<AssessmentResultDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);
  const [comparison, setComparison] = useState<AssessmentResultDetail[]>([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [sessionValidation, setSessionValidation] =
    useState<AssessmentValidation | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  // What the gate says about this answer key TODAY. It heads the screen, and
  // that is ALL it does.
  //
  // It used to govern every row as well, on the reasoning that all these links
  // point at one definition. They do -- but not at one MOMENT. A session
  // graded in June was graded by June's verdict, and the trainer's own report
  // prints exactly that: the snapshot stamped on the link at attach
  // (`link_validity_snapshot`, pm/assessment/services/attach_gate.py). Judging
  // a June session by today's verdict makes the two screens print different
  // words about the same score the first time anybody recomputes the gate --
  // and the admin screen is the one that is wrong.
  const [validity, setValidity] = useState<unknown>(undefined);

  useEffect(() => {
    let active = true;
    setValidity(undefined);
    fetchScenarioValidity(scenario.code)
      .then(response => {
        if (active) setValidity(response);
      })
      .catch(() => {
        // A screen that cannot read the gate says nothing about it rather than
        // implying the answer key is fine.
        if (active) setValidity(undefined);
      });
    return () => {
      active = false;
    };
  }, [scenario.code]);

  const screenGate = useMemo(() => readAnswerKeyGate(validity), [validity]);

  useEffect(() => {
    if (!selectedSession && links.length)
      setSelectedSession(links[0].session_id);
    if (
      selectedSession &&
      !links.some(link => link.session_id === selectedSession)
    ) {
      setSelectedSession(links[0]?.session_id);
    }
  }, [links, selectedSession]);

  const selectedLink = useMemo(
    () => links.find(link => link.session_id === selectedSession),
    [links, selectedSession],
  );

  /**
   * The verdict that actually graded the session on screen.
   *
   * Read from the results payload first -- `/assessment_sessions/<id>/results`
   * serves the link's own stamp under `validity`, which is the same document
   * the trainer's report reads -- then from the link row, for the rows that
   * carry one. `null` when neither did: an older link with no stamp and an
   * endpoint that did not send one are both "this screen was not told", and
   * the screen-level verdict is then the best fact available rather than a
   * second opinion.
   */
  const stampedGate = useMemo(
    () => readAnswerKeyGate(results) || readAnswerKeyGate(selectedLink),
    [results, selectedLink],
  );
  const sessionGate = stampedGate || screenGate;
  // Two banners saying the same sentence is how people learn to skip banners,
  // so the session's own verdict is shown only when it is not today's.
  const gradingDiffers =
    stampedGate != null &&
    (screenGate == null ||
      stampedGate.state !== screenGate.state ||
      stampedGate.verdict !== screenGate.verdict);

  const loadResults = async (sessionId = selectedSession) => {
    if (!sessionId) return;
    setResultLoading(true);
    setResultError('');
    setDetail(null);
    setSelectedPlayers([]);
    setComparison([]);
    try {
      setResults(await fetchAssessmentResults(sessionId));
    } catch (requestError) {
      setResults(null);
      setResultError((requestError as Error).message);
    } finally {
      setResultLoading(false);
    }
  };

  useEffect(() => {
    void loadResults(selectedSession);
    setSessionValidation(null);
  }, [selectedSession]);

  const attach = async () => {
    try {
      const values = await attachForm.validateFields();
      setAttaching(true);
      const response = await attachAssessmentToSession(
        values.session_id,
        scenario.code,
      );
      if (response.created) {
        message.success(
          `Attached ${scenario.code} to session ${values.session_id}.`,
        );
      } else {
        // WHAT HAPPENED IS THE BACKEND'S TO SAY. This screen used to announce
        // that "the existing link was left unchanged" over a call that, at the
        // time, re-resolved the link's instruments and re-stamped its verdict.
        // The attach endpoint now names the answer key that is actually on the
        // session, whether anything moved, and writes the sentence itself.
        message.info(
          response.detail ||
            `Session ${values.session_id} is already graded by answer key ` +
              `${response.scenario || 'another one'}. ${scenario.code} was ` +
              `not attached.`,
        );
      }
      attachForm.resetFields();
      await onLinksChanged();
      if (response.created) setSelectedSession(values.session_id);
    } catch (requestError) {
      if ((requestError as any)?.errorFields) return;
      message.error((requestError as Error).message);
    } finally {
      setAttaching(false);
    }
  };

  const loadDetail = async (row: AssessmentResult) => {
    if (!selectedSession) return;
    setDetailLoading(true);
    try {
      setDetail(await fetchAssessmentResultDetail(selectedSession, row.player));
    } catch (requestError) {
      message.error((requestError as Error).message);
    } finally {
      setDetailLoading(false);
    }
  };

  const compare = async () => {
    if (!selectedSession || selectedPlayers.length < 2) return;
    setComparisonLoading(true);
    try {
      setComparison(
        await Promise.all(
          selectedPlayers.map(player =>
            fetchAssessmentResultDetail(selectedSession, player),
          ),
        ),
      );
    } catch (requestError) {
      message.error((requestError as Error).message);
    } finally {
      setComparisonLoading(false);
    }
  };

  const validateForSession = async () => {
    if (!selectedSession) return;
    setValidationLoading(true);
    try {
      setSessionValidation(
        await validateAssessmentScenario(scenario.code, selectedSession),
      );
    } catch (requestError) {
      message.error((requestError as Error).message);
    } finally {
      setValidationLoading(false);
    }
  };

  const confirmRecompute = () => {
    if (!selectedSession) return;
    Modal.confirm({
      title: `Recompute assessment for session ${selectedSession}?`,
      content:
        'This invokes the existing recompute endpoint. It marks the existing link pending and queues the existing assessment task; it does not change the linked scenario or definition.',
      okText: 'Queue recompute',
      onOk: async () => {
        setRecomputing(true);
        try {
          await recomputeAssessment(selectedSession);
          message.success(
            'Assessment recompute queued through the existing runtime contract.',
          );
          await onLinksChanged();
          await loadResults(selectedSession);
        } catch (requestError) {
          message.error((requestError as Error).message);
        } finally {
          setRecomputing(false);
        }
      },
    });
  };

  const linkColumns: any[] = [
    { title: 'Session', dataIndex: 'session_id', width: 90 },
    {
      title: 'Class',
      dataIndex: 'klass_name',
      width: 180,
      render: (value: string) => <EllipsisText value={value} />,
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer_name',
      width: 150,
      render: (value: string) => <EllipsisText value={value} />,
    },
    { title: 'Session state', dataIndex: 'session_status_label', width: 120 },
    {
      title: 'Assessment job',
      dataIndex: 'assessment_status_label',
      width: 125,
      render: (value: string) => <QueueStatusTag label={value} />,
    },
    {
      title: 'Blueprint launch',
      width: 300,
      render: (_: unknown, row: AssessmentSessionLink) => (
        <SessionBlueprint link={row} />
      ),
    },
    {
      // Per row, from the row's own stamp -- never from the screen-level
      // verdict, which describes the answer key today rather than the grading
      // this session got. A row with no stamp says nothing at all.
      title: 'Answer key when graded',
      width: 230,
      render: (_: unknown, row: AssessmentSessionLink) => (
        <AnswerKeyGateTag gate={readAnswerKeyGate(row)} />
      ),
    },
    { title: 'Results', dataIndex: 'result_count', width: 80 },
    {
      title: 'Computed',
      dataIndex: 'computed_at',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: formatDateTime,
    },
    {
      title: 'Error',
      dataIndex: 'error',
      width: 260,
      render: (value: string) => <EllipsisText value={value} />,
    },
  ];

  const resultColumns: any[] = [
    { title: 'Rank', dataIndex: 'rank', width: 75 },
    {
      title: 'Candidate',
      dataIndex: 'player_name',
      fixed: 'left',
      width: 190,
      render: (value: string, row: AssessmentResult) => (
        <Tooltip title={value} placement="topLeft">
          <Typography.Link
            className="assessment-ellipsis"
            onClick={() => loadDetail(row)}
          >
            {value}
          </Typography.Link>
        </Tooltip>
      ),
    },
    { title: 'P1', dataIndex: 'pillar_1', width: 80, render: formatScore },
    { title: 'P2', dataIndex: 'pillar_2', width: 80, render: formatScore },
    { title: 'P3', dataIndex: 'pillar_3', width: 80, render: formatScore },
    {
      title: 'P4 prov.',
      dataIndex: 'pillar_4_provisional',
      width: 95,
      render: formatScore,
    },
    {
      title: 'P5 prov.',
      dataIndex: 'pillar_5_provisional',
      width: 95,
      render: formatScore,
    },
    {
      title: 'Official',
      dataIndex: 'official_composite',
      width: 95,
      render: formatScore,
    },
    {
      title: 'Illustrative',
      dataIndex: 'illustrative_composite',
      width: 100,
      render: formatScore,
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      width: 190,
      // Withheld while the answer key that produced these scores failed its
      // own quality audit with nobody signing for it: the failing check is
      // the finding that a candidate expressing no view can pass.
      render: (value: string) => (
        <AnswerKeyRating rating={value} gate={sessionGate} />
      ),
    },
    {
      // What the mark was earned on. The grid lists everybody who was in the
      // room, and pillar 1 -- a third of the official composite -- needs no
      // trade, so a row here no longer implies one. An OUTCOME: the trainer's
      // report and the client's dashboard print the same three words.
      title: 'Participation',
      dataIndex: 'participation',
      width: 210,
      render: (value: string, row: AssessmentResult) => (
        <>
          <ParticipationNote asTag participation={value} />
          <ConvictionCoverageNote asTag coverage={row.conviction_coverage} />
        </>
      ),
    },
    {
      title: 'Model version',
      dataIndex: 'model_version',
      width: 200,
      render: (value: string) => <EllipsisText code value={value} />,
    },
  ];

  const comparisonColumns: any[] = [
    {
      title: 'Candidate',
      dataIndex: 'player_name',
      width: 190,
      fixed: 'left',
      render: (value: string) => <EllipsisText value={value} />,
    },
    { title: 'Rank', dataIndex: ['summary', 'rank'], width: 75 },
    { title: 'P1', dataIndex: ['summary', 'pillar_1'], render: formatScore },
    { title: 'P2', dataIndex: ['summary', 'pillar_2'], render: formatScore },
    { title: 'P3', dataIndex: ['summary', 'pillar_3'], render: formatScore },
    {
      title: 'P4 prov.',
      dataIndex: ['summary', 'pillar_4_provisional'],
      render: formatScore,
    },
    {
      title: 'P5 prov.',
      dataIndex: ['summary', 'pillar_5_provisional'],
      render: formatScore,
    },
    {
      title: 'Official',
      dataIndex: ['summary', 'official_composite'],
      render: formatScore,
    },
    {
      title: 'Illustrative',
      dataIndex: ['summary', 'illustrative_composite'],
      render: formatScore,
    },
    {
      // Comparing candidates side by side is exactly where this matters: two
      // composites a point apart say nothing about one of them having been
      // earned without a single trade.
      title: 'Participation',
      dataIndex: ['summary', 'participation'],
      width: 210,
      render: (value: string, row: AssessmentResultDetail) => (
        <>
          <ParticipationNote asTag participation={value} />
          <ConvictionCoverageNote
            asTag
            coverage={row.summary?.conviction_coverage}
          />
        </>
      ),
    },
    {
      title: 'P&L ($m)',
      dataIndex: ['summary', 'total_pnl_m'],
      render: formatScore,
    },
    {
      title: 'Ending NAV ($m)',
      dataIndex: ['summary', 'ending_nav_m'],
      render: formatScore,
    },
  ];

  return (
    <div className="assessment-detail-stack">
      <VerdictBanner gate={screenGate} />
      <Card title="Attach to a session" size="small">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Existing attach semantics are preserved"
          description="A session can have one assessment link. If it is already linked, this action does not detach or repoint it. Attaching also locks a draft against management edits."
        />
        <Form form={attachForm} layout="inline" onFinish={attach}>
          <Form.Item
            name="session_id"
            label="Session ID"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} precision={0} />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<LinkOutlined />}
            loading={attaching}
          >
            Attach existing definition
          </Button>
        </Form>
      </Card>

      <Card title={`Linked sessions (${links.length})`} size="small">
        <Table
          rowKey="link_id"
          size="small"
          className="assessment-table"
          tableLayout="fixed"
          dataSource={links}
          columns={linkColumns}
          scroll={{ x: 1705 }}
          pagination={{ pageSize: 10 }}
          onRow={row => ({ onClick: () => setSelectedSession(row.session_id) })}
          rowClassName={row =>
            row.session_id === selectedSession ? 'assessment-selected-row' : ''
          }
        />
      </Card>

      <Card
        title="Session results"
        size="small"
        extra={
          <Space wrap>
            <Select
              placeholder="Choose linked session"
              style={{ width: 220 }}
              value={selectedSession}
              onChange={setSelectedSession}
              options={links.map(link => ({
                value: link.session_id,
                label: `Session ${link.session_id} · ${link.assessment_status_label}`,
              }))}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadResults()}
              disabled={!selectedSession}
            >
              Refresh results
            </Button>
            <Button onClick={validateForSession} disabled={!selectedSession}>
              Validate session fit
            </Button>
            <Button
              icon={<RetweetOutlined />}
              danger={selectedLink?.assessment_status_label === 'failed'}
              loading={recomputing}
              disabled={
                !selectedSession ||
                selectedLink?.assessment_status_label === 'running'
              }
              onClick={confirmRecompute}
            >
              Recompute
            </Button>
          </Space>
        }
      >
        {gradingDiffers && (
          <div>
            <Typography.Text type="secondary">
              {`Session ${selectedSession} was graded under a different verdict than the one this answer key carries today. What follows is the one it was graded under.`}
            </Typography.Text>
            <VerdictBanner gate={sessionGate} />
          </div>
        )}
        {selectedLink && (
          <Descriptions
            size="small"
            bordered
            column={3}
            className="assessment-summary"
          >
            <Descriptions.Item label="Assessment job">
              <QueueStatusTag label={selectedLink.assessment_status_label} />
            </Descriptions.Item>
            <Descriptions.Item label="Definition">
              {/* THE ANSWER KEY BY NAME, not by version number (D5). There are
                  no version numbers any more -- a key IS its inputs -- so
                  `conviction-v0` beside a code named nothing a reader could
                  look up. The name is what the blueprint page shows, and the
                  retirement note (D8) is why they will not find it there. */}
              <Space direction="vertical" size={0}>
                <span>
                  {selectedLink.scenario_name || selectedLink.scenario}
                  {selectedLink.scenario_name
                    ? ` · ${selectedLink.scenario}`
                    : ''}
                </span>
                {sessionGate?.retiredNote && (
                  <Typography.Text type="secondary">
                    {sessionGate.retiredNote}
                  </Typography.Text>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Blueprint">
              <SessionBlueprint link={selectedLink} />
            </Descriptions.Item>
            <Descriptions.Item label="Computed">
              {formatDateTime(selectedLink.computed_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Result count">
              {selectedLink.result_count}
            </Descriptions.Item>
            <Descriptions.Item label="Runtime error">
              {selectedLink.error || '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
        {sessionValidation && (
          <Card
            size="small"
            title="Definition + selected session diagnostics"
            className="assessment-inner-card"
          >
            <ValidationPanel
              validation={sessionValidation}
              loading={validationLoading}
            />
          </Card>
        )}
        {resultError && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={resultError}
          />
        )}
        <div className="assessment-table-toolbar">
          <Space>
            <Typography.Text type="secondary">
              Select 2–4 candidates for a side-by-side comparison.
            </Typography.Text>
            <Button
              onClick={compare}
              loading={comparisonLoading}
              disabled={selectedPlayers.length < 2}
            >
              Compare selected ({selectedPlayers.length})
            </Button>
          </Space>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              disabled={!selectedSession}
              onClick={() =>
                selectedSession &&
                downloadAssessmentResults(selectedSession, 'json').catch(
                  error => message.error(error.message),
                )
              }
            >
              Results JSON
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={!selectedSession}
              onClick={() =>
                selectedSession &&
                downloadAssessmentResults(selectedSession, 'csv').catch(error =>
                  message.error(error.message),
                )
              }
            >
              Results CSV
            </Button>
          </Space>
        </div>
        <Table
          rowKey="player"
          className="assessment-table"
          tableLayout="fixed"
          loading={resultLoading}
          dataSource={results?.results || []}
          columns={resultColumns}
          scroll={{ x: 1250 }}
          rowSelection={{
            selectedRowKeys: selectedPlayers,
            preserveSelectedRowKeys: false,
            onChange: keys => setSelectedPlayers(keys.slice(0, 4).map(Number)),
            getCheckboxProps: row => ({
              disabled:
                selectedPlayers.length >= 4 &&
                !selectedPlayers.includes(row.player),
            }),
          }}
          onRow={row => ({ onDoubleClick: () => loadDetail(row) })}
          pagination={{ pageSize: 20 }}
        />

        {comparison.length > 0 && (
          <Card
            size="small"
            title="Candidate comparison"
            className="assessment-inner-card"
          >
            <Table
              rowKey="player"
              size="small"
              className="assessment-table"
              tableLayout="fixed"
              pagination={false}
              dataSource={comparison}
              columns={comparisonColumns}
              scroll={{ x: 1150 }}
            />
            <Divider />
            <Tabs
              items={comparison.map(item => ({
                key: String(item.player),
                label: item.player_name,
                children: (
                  <Tabs
                    items={['1', '2', '3', '4', '5'].map(pillar => ({
                      key: pillar,
                      label: `Pillar ${pillar}`,
                      children: <PillarDetail detail={item} pillar={pillar} />,
                    }))}
                  />
                ),
              }))}
            />
          </Card>
        )}
      </Card>

      <Modal
        title={
          detail ? `${detail.player_name} · result detail` : 'Result detail'
        }
        open={Boolean(detail) || detailLoading}
        onCancel={() => setDetail(null)}
        footer={null}
        width="90vw"
        loading={detailLoading}
        destroyOnHidden
      >
        {detail && (
          <>
            <Descriptions
              bordered
              size="small"
              column={4}
              className="assessment-summary"
            >
              <Descriptions.Item label="Rank">
                {detail.summary.rank ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Rating">
                <AnswerKeyRating
                  rating={detail.summary.rating}
                  gate={sessionGate}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Participation">
                <ParticipationNote
                  participation={detail.summary.participation}
                />
                <div>
                  <ConvictionCoverageNote
                    coverage={detail.summary.conviction_coverage}
                  />
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Official">
                {formatScore(detail.summary.official_composite)}
              </Descriptions.Item>
              <Descriptions.Item label="Illustrative">
                {formatScore(detail.summary.illustrative_composite)}
              </Descriptions.Item>
              <Descriptions.Item label="P&L ($m)">
                {formatScore(detail.summary.total_pnl_m)}
              </Descriptions.Item>
              <Descriptions.Item label="Ending NAV ($m)">
                {formatScore(detail.summary.ending_nav_m)}
              </Descriptions.Item>
              <Descriptions.Item label="Model" span={2}>
                {detail.model_version}
              </Descriptions.Item>
            </Descriptions>
            <Tabs
              items={['1', '2', '3', '4', '5'].map(pillar => ({
                key: pillar,
                label: `Pillar ${pillar}`,
                children: <PillarDetail detail={detail} pillar={pillar} />,
              }))}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default SessionsResults;
